import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ClickableStatCard from "@/components/ClickableStatCard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Trophy,
  SplitSquareVertical,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Download,
  MailOpen,
  Users,
  Pause,
  Play,
  Edit3,
  Calendar,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";
import { useAuth } from "@/context/AuthContext";
import { formatInUserTimezone } from "@/lib/timezone";

const StatusPill = ({ status, progress, rateLimitWaiting }) => {
  const map = {
    draft: { bg: "#FEF3C7", text: "#92400E", label: "Draft" },
    failed: { bg: "#FEE2E2", text: "#991B1B", label: "Send failed" },
    scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
    sending: {
      bg: rateLimitWaiting ? "#FFEDD5" : "#FEF3C7",
      text: rateLimitWaiting ? "#C2410C" : "#B45309",
      label: rateLimitWaiting
        ? (progress?.total ? `Waiting for limit ${progress.sent}/${progress.total}` : "Waiting for limit")
        : (progress?.total ? `Sending ${progress.sent}/${progress.total}` : "Sending"),
    },
    paused: { bg: "#FCE7F3", text: "#9D174D", label: progress?.total ? `Paused ${progress.sent}/${progress.total}` : "Paused" },
    sent: { bg: "#D1FAE5", text: "#065F46", label: "Sent" },
    testing: { bg: "#E0E7FF", text: "#3730A3", label: "Testing" },
  };
  const s = map[status] || { bg: "#F3F4F6", text: "#374151", label: status };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
};

function formatSubtitle(c, userTz = "UTC") {
  const audience = c.audience_name ? ` • ${c.audience_name}` : "";
  if (c.status === "sending") {
    const sent = c.send_progress?.sent ?? 0;
    const total = c.send_progress?.total ?? "?";
    if (c.rate_limit_waiting) {
      return `Waiting for delivery rate limit — ${sent} / ${total} sent so far${audience}`;
    }
    return `Sending in progress — ${sent} / ${total} emails${audience}`;
  }
  if (c.status === "paused") {
    const sent = c.send_progress?.sent ?? 0;
    const total = c.send_progress?.total;
    const progress = total ? ` — ${sent} / ${total} sent so far` : "";
    const from = c.paused_from === "scheduled" ? "Scheduled send paused" : "Send paused";
    return `${from}${progress}${audience}`;
  }
  if (c.status === "scheduled" && c.scheduled_at) {
    return `Scheduled for ${formatInUserTimezone(c.scheduled_at, userTz)} (${userTz})${audience}`;
  }
  if (c.status === "draft") {
    return `Draft${audience}`;
  }
  if (c.status === "failed") {
    return `Send failed${audience}`;
  }
  if (c.status === "testing") {
    return `A/B test in progress${audience}`;
  }
  if (c.sent_at) {
    return `Sent ${formatInUserTimezone(c.sent_at, userTz)}${audience}`;
  }
  return c.audience_name || "";
}

export default function CampaignAnalyticsPage() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const userTz = user?.timezone || "UTC";
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);

  const load = useCallback(() => api.get(`/campaigns/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const shouldPollSendStatus = c && ["sending", "paused", "scheduled", "testing"].includes(c.status);

  useEffect(() => {
    if (!shouldPollSendStatus) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get(`/campaigns/${id}/send-status`);
        setC((prev) => {
          if (!prev) return prev;
          const terminal = !["sending", "paused", "scheduled", "testing"].includes(data.status);
          if (terminal && prev.status !== data.status) {
            if (data.status === "failed" && data.send_error) {
              toast.error(data.send_error);
            }
            load();
          }
          return {
            ...prev,
            status: data.status,
            paused_from: data.paused_from,
            send_progress: data.send_progress,
            send_error: data.send_error,
            rate_limit_waiting: data.rate_limit_waiting,
            recipients: data.recipients ?? prev.recipients,
            opens: data.opens ?? prev.opens,
            unique_opens: data.unique_opens ?? prev.unique_opens,
            clicks: data.clicks ?? prev.clicks,
            bounces: data.bounces ?? prev.bounces,
            unsubscribes: data.unsubscribes ?? prev.unsubscribes,
          };
        });
      } catch {
        /* ignore transient poll errors */
      }
    };
    poll();
    const timer = setInterval(poll, 10000);
    return () => clearInterval(timer);
  }, [shouldPollSendStatus, id, load]);

  const pause = async () => {
    const ok = await confirm({
      title: "Pause campaign?",
      description: "Sending will stop after the current batch finishes.",
      confirmText: "Pause",
    });
    if (!ok) return;
    setPausing(true);
    try {
      await api.post(`/campaigns/${id}/pause`);
      toast.success("Campaign paused");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to pause");
    } finally {
      setPausing(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.post(`/campaigns/${id}/resume`);
      toast.success("Campaign resumed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resume");
    } finally {
      setResuming(false);
    }
  };

  const decideWinner = async (winner) => {
    const ok = await confirm({
      title: "Send winning variant?",
      description: `Send variant ${winner} to the remaining audience?`,
      confirmText: "Send variant",
    });
    if (!ok) return;
    setDeciding(true);
    try {
      await api.post(`/campaigns/${id}/decide-winner?winner=${winner}`);
      toast.success(`Variant ${winner} sent to remaining audience`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setDeciding(false);
    }
  };

  const exportOpens = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/campaigns/${id}/export/opens`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mailwave-opens-${id.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Opens exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!c) return <div className="p-12 text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;

  const recipientCount = c.recipients || c.send_progress?.total || 0;
  const pct = (n) => (recipientCount ? Math.round((n / recipientCount) * 1000) / 10 : 0);
  const variants = c.variants || [];
  const isMulti = c.mode && c.mode !== "single" && variants.length > 1;
  const uniqueOpens = c.unique_opens ?? c.opens ?? 0;
  const canEngage = c.status === "sent" || c.status === "testing" || uniqueOpens > 0 || (c.clicks || 0) > 0;
  const sendPct = c.send_progress?.total
    ? Math.round(((c.send_progress.sent || 0) / c.send_progress.total) * 100)
    : 0;

  const data = [
    { name: "Opened", value: uniqueOpens, color: "#002FA7" },
    { name: "Not opened", value: Math.max(recipientCount - uniqueOpens - (c.bounces || 0), 0), color: "#E5E7EB" },
    { name: "Bounced", value: c.bounces || 0, color: "#E0000F" },
  ];

  return (
    <div data-testid="campaign-analytics">
      <PageHeader
        title={c.name}
        subtitle={formatSubtitle(c, userTz)}
        action={
          <div className="flex flex-wrap gap-2">
            {(c.status === "sending" || c.status === "scheduled") && (
              <Button
                variant="outline"
                onClick={pause}
                disabled={pausing}
                className="rounded-sm font-semibold"
                data-testid="pause-campaign-button"
              >
                <Pause className="h-4 w-4 mr-2" />
                {pausing ? "Pausing..." : "Pause"}
              </Button>
            )}
            {c.status === "paused" && (
              <Button
                onClick={resume}
                disabled={resuming}
                className="rounded-sm btn-brand font-semibold"
                data-testid="resume-campaign-button"
              >
                <Play className="h-4 w-4 mr-2" />
                {resuming ? "Resuming..." : "Resume"}
              </Button>
            )}
            {(c.status === "draft" || c.status === "failed" || c.status === "scheduled" || c.status === "paused") && (
              <Link to={`/campaigns/${id}/edit`}>
                <Button variant="outline" className="rounded-sm font-semibold" data-testid="edit-campaign-button">
                  <Edit3 className="h-4 w-4 mr-2" /> Edit
                </Button>
              </Link>
            )}
            {(c.status === "sent" || c.status === "testing") && (
              <Button
                variant="outline"
                onClick={exportOpens}
                disabled={exporting || uniqueOpens === 0}
                className="rounded-sm font-semibold"
                data-testid="export-opens-button"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? "Exporting..." : "Export opens (CSV)"}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        <Link to="/campaigns" className="inline-flex items-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to campaigns
        </Link>

        <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <StatusPill status={c.status} progress={c.send_progress} rateLimitWaiting={c.rate_limit_waiting} />
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              <span className="font-semibold text-neutral-900 dark:text-neutral-50">{c.subject}</span>
              {c.from_email && (
                <span className="text-neutral-500 dark:text-neutral-400"> · {c.from_name} &lt;{c.from_email}&gt;</span>
              )}
            </div>
          </div>
          {c.scheduled_at && (c.status === "scheduled" || c.status === "paused") && (
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <Calendar className="h-4 w-4" />
              {formatInUserTimezone(c.scheduled_at, userTz)} ({userTz})
            </div>
          )}
        </div>

        {(c.status === "sending" || (c.status === "paused" && c.send_progress?.total)) && (
          <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm space-y-3" data-testid="send-progress-panel">
            <div className="flex justify-between text-sm">
              <span className="font-semibold">Send progress</span>
              <span className="text-neutral-500 dark:text-neutral-400">
                {c.send_progress?.sent ?? 0} / {c.send_progress?.total ?? "?"} sent
                {(c.send_progress?.failed ?? 0) > 0 && ` · ${c.send_progress.failed} failed`}
              </span>
            </div>
            <Progress value={sendPct} className="h-2" />
          </div>
        )}

        {c.send_error && (
          <div className={`lift-card p-4 rounded-sm text-sm ${c.rate_limit_waiting ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200" : "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"}`}>
            {c.rate_limit_waiting ? "Status" : "Send error"}: {c.send_error}
          </div>
        )}

        {c.status === "testing" && isMulti && (
          <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm border-l-4" style={{ borderLeftColor: "var(--brand-primary)" }} data-testid="ab-decision-panel">
            <div className="flex items-center gap-2 mb-4">
              <SplitSquareVertical className="h-5 w-5" style={{ color: "var(--brand-primary)" }} />
              <h3 className="font-display font-bold text-2xl tracking-tight">Pick your winner</h3>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">A test send has gone out. Choose the best-performing variant and we'll send it to the remaining audience.</p>
            <div className={`grid gap-4 ${variants.length <= 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
              {variants.map((v) => (
                <div key={v.label} className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-5">
                  <p className="label-overline mb-2">Variant {v.label}</p>
                  <div className="font-display font-bold text-base mb-3 line-clamp-2">{v.subject}</div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <div className="text-2xl font-display font-black">{v.unique_opens || v.opens || 0}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1"><Eye className="h-3 w-3" /> unique opens</div>
                    </div>
                    <div>
                      <div className="text-2xl font-display font-black">{v.unique_clicks || v.clicks || 0}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> clicks</div>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">Sent to {v.recipients} recipients</div>
                  <Button onClick={() => decideWinner(v.label)} disabled={deciding} className="w-full rounded-sm btn-brand font-semibold" data-testid={`winner-${v.label.toLowerCase()}-button`}>
                    <Trophy className="h-4 w-4 mr-2" /> Send {v.label} to rest
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {c.winner_label && (
          <div className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm flex items-center gap-3">
            <Trophy className="h-5 w-5" style={{ color: "var(--accent-green)" }} />
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Winner</div>
              <div className="font-semibold">Variant {c.winner_label}: {variants.find((v) => v.label === c.winner_label)?.subject}</div>
            </div>
          </div>
        )}

        {(c.bot_open_events > 0 || c.privacy_proxy_open_events > 0) && (
          <div className="lift-card bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 rounded-sm text-sm text-neutral-600 dark:text-neutral-400">
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">Open quality: </span>
            Stats count human opens only (security scanners and bot pixels are filtered out).
            {c.bot_open_events > 0 && (
              <span> Filtered bot/scanner pixel loads: {c.bot_open_events.toLocaleString()}.</span>
            )}
            {c.privacy_proxy_open_events > 0 && (
              <span> Gmail/Yahoo proxy opens (real users, privacy cache): {c.privacy_proxy_open_events.toLocaleString()}.</span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <ClickableStatCard label="Recipients" value={recipientCount || "—"} Icon={Users} testId="metric-recipients" />
          <ClickableStatCard
            label="Unique opens"
            value={uniqueOpens}
            suffix={recipientCount ? ` ${pct(uniqueOpens)}%` : ""}
            Icon={MailOpen}
            to={canEngage ? `/campaigns/${id}/engagement/opens` : undefined}
            accent="var(--brand-primary)"
            testId="metric-opens"
          />
          <ClickableStatCard
            label="Clicks"
            value={c.clicks || 0}
            suffix={recipientCount ? ` ${pct(c.clicks || 0)}%` : ""}
            Icon={MousePointerClick}
            to={canEngage ? `/campaigns/${id}/engagement/clicks` : undefined}
            accent="var(--accent-red)"
            testId="metric-clicks"
          />
          <ClickableStatCard
            label="Bounces"
            value={c.bounces || 0}
            suffix={recipientCount ? ` ${pct(c.bounces || 0)}%` : ""}
            Icon={AlertTriangle}
            to={canEngage ? `/campaigns/${id}/engagement/bounces` : undefined}
            accent="#E0000F"
            testId="metric-bounces"
          />
          <ClickableStatCard
            label="Unsubscribes"
            value={c.unsubscribes || 0}
            suffix={recipientCount ? ` ${pct(c.unsubscribes || 0)}%` : ""}
            Icon={AlertTriangle}
            to={canEngage ? `/campaigns/${id}/engagement/unsubscribes` : undefined}
            accent="#3730A3"
            testId="metric-unsubscribes"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm">
            <p className="label-overline mb-2">Engagement</p>
            <h3 className="font-display font-bold text-2xl tracking-tight mb-6">Delivery breakdown</h3>
            {recipientCount > 0 ? (
              <>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={2} dataKey="value">
                        {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: "2px", fontFamily: "Karla", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontFamily: "Karla", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-neutral-100 dark:border-neutral-800 text-center text-sm">
                  {canEngage ? (
                    <>
                      <Link to={`/campaigns/${id}/engagement/opens`} className="block p-3 rounded-sm hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                        <div className="font-display font-black text-2xl text-[#002FA7]">{uniqueOpens}</div>
                        <div className="text-neutral-500 dark:text-neutral-400">Opens → view list</div>
                      </Link>
                      <Link to={`/campaigns/${id}/engagement/clicks`} className="block p-3 rounded-sm hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                        <div className="font-display font-black text-2xl text-[#E0000F]">{c.clicks || 0}</div>
                        <div className="text-neutral-500 dark:text-neutral-400">Clicks → view list</div>
                      </Link>
                      <Link to={`/campaigns/${id}/engagement/bounces`} className="block p-3 rounded-sm hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                        <div className="font-display font-black text-2xl text-neutral-600 dark:text-neutral-400">{c.bounces || 0}</div>
                        <div className="text-neutral-500 dark:text-neutral-400">Bounces → view list</div>
                      </Link>
                      <Link to={`/campaigns/${id}/engagement/unsubscribes`} className="block p-3 rounded-sm hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                        <div className="font-display font-black text-2xl text-[#3730A3]">{c.unsubscribes || 0}</div>
                        <div className="text-neutral-500 dark:text-neutral-400">Unsubscribes → view list</div>
                      </Link>
                    </>
                  ) : (
                    <p className="col-span-full text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">Stats will appear once emails are delivered.</p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 py-8 text-center">No recipients yet — send or schedule this campaign to see engagement stats.</p>
            )}
          </div>

          <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm">
            <p className="label-overline mb-2">Email</p>
            <h3 className="font-display font-bold text-2xl tracking-tight mb-6">Campaign content</h3>
            {isMulti ? (
              <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                {variants.map((v) => (
                  <div key={v.label} className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="label-overline">Variant {v.label}{v.label === c.winner_label && " (winner)"}</span>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">{v.recipients || 0} sent · {v.unique_opens || v.opens || 0} opens · {v.clicks || 0} clicks</div>
                    </div>
                    <div className="font-semibold text-sm">{v.subject}</div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Subject</div>
                <div className="font-semibold mb-3">{c.subject}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">From</div>
                <div className="text-sm mb-1">{c.from_name} &lt;{c.from_email}&gt;</div>
                {c.reply_to && <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">Reply-to: {c.reply_to}</div>}
                {c.content ? (
                  <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4 max-h-64 overflow-auto email-preview text-sm" dangerouslySetInnerHTML={{ __html: c.content }} />
                ) : (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-800 pt-4">No content preview available.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
